import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { STAGE_LABEL, ROLE_LABEL } from "@/constants/roles";
import { useT } from "@/i18n/LanguageContext";

// Flagged order → "↩ Continue → <stage it came from>" with a mandatory note.
// Progress at that station is kept; the station gets a dashboard alert.
export function FlagResumeButton({ order, onDone }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const { data: origin } = useQuery({
    queryKey: ["flag-origin", order.id, order.status],
    queryFn: async () => (await api.get(`/orders/${order.id}/flag-origin`)).data,
    enabled: order.status === "flagged",
  });
  const resume = useMutation({
    mutationFn: () => api.post(`/orders/${order.id}/flag-resume`, { note: note.trim() }),
    onSuccess: () => {
      qc.invalidateQueries();
      toast.success(`${order.po_number} ${t("resumed at")} ${t(STAGE_LABEL[origin.stage] || origin.stage)}`);
      setOpen(false); setNote("");
      onDone?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || t("Could not resume order")),
  });
  if (!origin?.stage) return null;
  const stageLabel = t(STAGE_LABEL[origin.stage] || origin.stage);
  const stationLabel = t(ROLE_LABEL[origin.station_role] || origin.station_role || "");
  return (
    <>
      <Button
        data-testid="flagged-resume-btn"
        onClick={() => setOpen(true)}
        className="bg-slate-900 hover:bg-slate-800 text-white"
        title={t("Send back to the station it came from, keeping its progress")}
      >
        ↩ {t("Continue")} → {stageLabel}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="flag-resume-dialog">
          <DialogHeader>
            <DialogTitle>↩ {t("Continue")} → {stageLabel}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              {t("The order goes back to")} <b>{stageLabel}</b> {t("with all its progress kept")}. <b>{stationLabel}</b> {t("will get an alert with your note")}.
            </p>
            {order.flag_reason && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <span className="uppercase tracking-widest font-mono text-[10px] text-amber-700">{t("Flag reason")}</span>
                <div className="font-medium mt-0.5">{order.flag_reason_label || order.flag_reason}</div>
                {(order.flagged_by || order.flagged_at) && (
                  <div className="text-[11px] text-amber-800 mt-0.5">{order.flagged_by === "auto:inventory-match" ? t("Flagged automatically by the inventory check") : `${t("Flagged by")} ${order.flagged_by || "—"}`}{order.flagged_at ? ` · ${new Date(order.flagged_at).toLocaleString()}` : ""}</div>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">{t("Note for the station")} *</Label>
              <Textarea
                data-testid="flag-resume-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={t("e.g. Codes verified — continue as is")}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={resume.isPending}>{t("Cancel")}</Button>
            <Button
              data-testid="flag-resume-confirm"
              onClick={() => resume.mutate()}
              disabled={resume.isPending || !note.trim()}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {resume.isPending ? t("Sending…") : `↩ ${t("Continue")} → ${stageLabel}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
